import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { PlatformCall } from './common';

/**
 * Whether a uKnowva instance's first sign-in to payroll waits for an operator
 * to approve it. Until it is set here, the server follows
 * TENANT_PROVISIONING_MODE from its environment. Also how many rows setup
 * fetches from each uKnowva master.
 */

type Scope = 'ANY' | 'ALLOWLIST';

type Draft = Pick<OnboardingPolicy, 'approval_required' | 'auto_approve_scope' | 'master_fetch_limit'>;

interface OnboardingPolicy {
  approval_required: boolean;
  auto_approve_scope: Scope;
  master_fetch_limit: number;
  source: 'console' | 'environment';
  env_mode: string;
  env_allowlist: string[];
}

export function UknowvaOnboardingSettings({ call }: { call: PlatformCall }) {
  const [policy, setPolicy] = useState<OnboardingPolicy | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = (p: OnboardingPolicy) => {
    setPolicy(p);
    setDraft({
      approval_required: p.approval_required,
      auto_approve_scope: p.auto_approve_scope,
      master_fetch_limit: p.master_fetch_limit,
    });
  };

  const load = useCallback(async () => {
    setErr(null);
    try {
      apply(((await call('/settings/onboarding')) as { policy: OnboardingPolicy }).policy);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (body: Record<string, unknown>) => {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      apply(
        ((await call('/settings/onboarding', {
          method: 'PUT',
          body: JSON.stringify(body),
        })) as { policy: OnboardingPolicy }).policy,
      );
      setSaved(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!draft || !policy) {
    return err ? (
      <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const approvalDirty =
    draft.approval_required !== policy.approval_required ||
    draft.auto_approve_scope !== policy.auto_approve_scope;
  const limitDirty = draft.master_fetch_limit !== policy.master_fetch_limit;
  const limitValid =
    Number.isInteger(draft.master_fetch_limit) &&
    draft.master_fetch_limit >= 0 &&
    draft.master_fetch_limit <= 1_000_000;
  const dirty = approvalDirty || limitDirty;
  // Only what changed, so editing the row limit does not also pin the
  // approval rule to this console when it is following the environment.
  const changes = {
    ...(approvalDirty
      ? { approval_required: draft.approval_required, auto_approve_scope: draft.auto_approve_scope }
      : {}),
    ...(limitDirty ? { master_fetch_limit: draft.master_fetch_limit } : {}),
  };
  const allowlist = policy.env_allowlist.length ? policy.env_allowlist.join(', ') : 'empty';

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 720 }}>
      <Typography variant="subtitle2" gutterBottom>uKnowva onboarding</Typography>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {saved && !dirty && <Alert severity="success" sx={{ mb: 2 }}>Saved.</Alert>}

      <FormControlLabel
        control={
          <Switch
            checked={draft.approval_required}
            onChange={(e) => setDraft({ ...draft, approval_required: e.target.checked })}
          />
        }
        label="First sign-in from a uKnowva instance needs approval"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        On: a new instance waits under Onboarding requests until an operator approves it. Off: its
        workspace is created and setup starts as soon as someone signs in from it.
      </Typography>

      {!draft.approval_required && (
        <>
          <RadioGroup
            value={draft.auto_approve_scope}
            onChange={(e) => setDraft({ ...draft, auto_approve_scope: e.target.value as Scope })}
            sx={{ ml: 1 }}
          >
            <FormControlLabel value="ANY" control={<Radio size="small" />} label="Any uKnowva instance" />
            <FormControlLabel
              value="ALLOWLIST"
              control={<Radio size="small" />}
              label={`Only hosts on TENANT_DOMAIN_ALLOWLIST (${allowlist}); others still need approval`}
            />
          </RadioGroup>
          {draft.auto_approve_scope === 'ANY' && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              The sign-in that starts setup cannot be verified yet, so anyone can make one that names
              any instance and an empty workspace database is created for it. The sign-in key and the
              employee import still need that instance&apos;s own uKnowva API token.
            </Alert>
          )}
        </>
      )}

      <TextField
        label="Rows fetched per master during setup"
        type="number"
        size="small"
        value={Number.isNaN(draft.master_fetch_limit) ? '' : draft.master_fetch_limit}
        onChange={(e) =>
          setDraft({
            ...draft,
            master_fetch_limit: e.target.value === '' ? Number.NaN : Number(e.target.value),
          })
        }
        error={!limitValid}
        helperText={
          limitValid
            ? '0 = every row. Employees and attendance are always fetched in full. A full sync from the workspace dashboard brings the rest of a master.'
            : 'A whole number from 0 to 1,000,000.'
        }
        inputProps={{ min: 0, max: 1_000_000, step: 100 }}
        sx={{ mt: 2, maxWidth: 360 }}
        fullWidth
      />

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          disabled={busy || !dirty || !limitValid}
          onClick={() => void save(changes)}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {policy.source === 'console' && (
          <Button size="small" disabled={busy} onClick={() => void save({ approval_required: null })}>
            Use server default ({policy.env_mode})
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          {policy.source === 'console'
            ? 'Set from this console.'
            : `Following the server setting TENANT_PROVISIONING_MODE=${policy.env_mode}.`}
        </Typography>
      </Stack>
    </Paper>
  );
}
